<template>
  <div :class="b({ variant, expandBadges })">
    <div :class="b('top')">
      <e-badge-labels
        :class="b('professions')"
        :labels="course.professions"
        @label-clicked="onProfessionFilterClicked"
      />
      <component
        :is="course.availableOnSite ? 'a' : 'span'"
        :href="course.availableOnSite ? course.url : undefined"
        :class="b('image')"
      >
        <e-picture
          v-if="course.image"
          :sizes="sizes"
          :srcset="course.image.srcset"
          :fallback="course.image.fallback"
          :alt="course.image.alt"
          :ratio="375 / 234"
          :loading="disableLazyLoading ? 'auto' : 'lazy'"
          height="234"
          width="375"
        />
      </component>
    </div>
    <div :class="b('badges')">
      <e-badge
        v-if="course.cePointsAvailable && course.cePoints"
        :always-expanded="expandBadges"
      >
        {{ $t('c-course-tile.ceBadge') }}
        <template #content> +{{ course.cePoints }} {{ $t('c-course-tile.cePoints') }} </template>
      </e-badge>
      <e-badge
        v-if="course.badgePoints"
        :always-expanded="expandBadges"
        variant="gradient"
      >
        <e-icon-sprite
          icon="i-star"
          size="15"
        />
        <template #content> +{{ course.badgePoints }} {{ $t('c-course-tile.points') }} </template>
      </e-badge>
      <e-badge
        v-if="course.localPoints"
        :always-expanded="expandBadges"
        variant="local-points"
      >
        {{ $t('c-course-tile.localPointsShort') }}
        <template #content>
          {{ $t('c-course-tile.localPoints', course.localPoints) }}
        </template>
      </e-badge>
    </div>
    <div :class="b('content')">
      <div :class="b('content-top')">
        <div :class="b('date')">
          {{ date || $t('c-course-tile.onDemand') }}
        </div>
        <div :class="b('meta')">
          <div
            v-if="course.courseType"
            :class="b('type')"
          >
            {{ course.courseType }}
          </div>
          <e-course-availability
            v-if="showAvailability && course.availableSeats !== null"
            :class="b('seats')"
            :available-seats="course.availableSeats"
          />
        </div>
        <component
          :is="course.availableOnSite ? 'a' : 'div'"
          :href="course.availableOnSite ? course.url : undefined"
        >
          <div :class="b('title')">
            {{ course.name }}
          </div>
        </component>
        <c-overflow-text-list
          ref="lecturers"
          :class="b('lecturers')"
          :entries="lecturers"
          :prefix="$t('c-course-tile.lecturers-prefix')"
        />
      </div>
      <div :class="b('content-bottom')">
        <div
          v-if="course.location"
          :class="b('location')"
        >
          <component
            :is="!!mapsLink ? 'a' : 'span'"
            :href="mapsLink ? mapsLink : undefined"
            :class="b('location-link')"
            target="_blank"
            rel="noopener noreferrer"
          >
            <e-icon-sprite
              v-if="mapsLink"
              icon="i-pin"
              size="20"
            />
            {{ [course.location.city, course.location.country].filter(Boolean).join(', ') }}
          </component>
        </div>
        <div
          v-if="course.availableOnSite"
          :class="b('price')"
        >
          <template v-if="isFreeCourse">
            {{ $t('c-course-tile.isFree') }}
          </template>
          <template v-else-if="isPriceOnDemand">
            {{ $t('c-course-price.onDemandShortText') }}
          </template>
          <template v-else>
            {{ `${course.price} ${course.priceSuffix ? course.priceSuffix : ''}` }}
            <div
              v-if="course.priceTextBelow"
              :class="b('price-suffix')"
            >
              {{ course.priceTextBelow }}
            </div>
          </template>
        </div>
      </div>
    </div>
    <div :class="b('actions')">
      <template v-if="course.availableOnSite">
        <button
          v-if="showCancelButton"
          :class="b('cta', { cancel: true })"
          :aria-label="cancelButtonLabel || $t('c-course-tile.cancelButton')"
          type="button"
          @click="onCancelButtonClick"
        >
          <e-icon-sprite
            icon="i-close"
            size="20"
          />
        </button>
        <button
          v-if="showFavouriteButton"
          :class="b('cta')"
          :disabled="courseFavouritesStore.runningRequests.apiUpdateFavourite"
          :aria-label="$t('c-course-tile.favouriteButton')"
          type="button"
          @click="onClickFavourite"
        >
          <e-icon-sprite
            :icon="isFavourite ? 'i-heart-24--filled' : 'i-heart-24'"
            size="20"
          />
        </button>
        <a
          v-if="date"
          :class="b('cta')"
          :href="$api.getUrl('course-ical', { courseId: course.id })"
          :title="$t('c-course-tile.calendarButton')"
          download
        >
          <e-icon-sprite
            icon="i-calendar"
            size="20"
          />
        </a>
        <a
          :href="course.url"
          :class="b('cta', { details: true })"
        >
          {{ $t('c-course-tile.detailLink') }}
        </a>
      </template>
      <div
        v-else
        :class="b('cta', { disabled: true })"
      >
        {{ $t('c-course-tile.detailLinkNotAvailable') }}
      </div>
    </div>
  </div>
</template>

<script>
  import useCourseFavouritesStore from '@/stores/course-favourites';
  import cOverflowTextList from '@/components/c-overflow-text-list.vue';
  import eBadgeLabels from '@/elements/e-badge-labels.vue';
  import eBadge from '@/elements/e-badge.vue';
  import eCourseAvailability from '@/elements/e-course-availability.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import ePicture from '@/elements/e-picture.vue';
  import { getCourseDateFormatted } from '@/helpers/format-date-time.js';

  /**
   * Renders a course tile.
   */
  export default {
    name: 'c-course-tile',

    components: {
      eIconSprite,
      ePicture,
      eCourseAvailability,
      eBadge,
      cOverflowTextList,
      eBadgeLabels,
    },
    // mixins: [],

    props: {
      /**
       * Expects a course data object.
       */
      course: {
        type: Object,
        required: true,
      },

      /**
       * Allows to define a styling variant for the tile.
       */
      variant: {
        type: String,
        default: 'default',
        validator(value) {
          return ['default', 'wide'].includes(value);
        },
      },

      /**
       * Allows to show/hide availability of the course.
       */
      showAvailability: {
        type: Boolean,
        default: true,
      },

      /**
       * Defines if favourite button is shown.
       */
      showFavouriteButton: {
        type: Boolean,
        default: true,
      },

      /**
       * Defines if badges are always expanded.
       */
      expandBadges: {
        type: Boolean,
        default: false,
      },

      /**
       * Allows to display a cancel button in the action section.
       */
      showCancelButton: {
        type: Boolean,
        default: false,
      },

      /**
       * Allows to set a specific cancel button label.
       */
      cancelButtonLabel: {
        type: String,
        default: null,
      },

      /**
       * Allows disabling of lazy loading for the current instance to improve LCP.
       */
      disableLazyLoading: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['cancel', 'professionFilterClicked'],

    setup() {
      return {
        courseFavouritesStore: useCourseFavouritesStore(),
      };
    },
    data() {
      return {
        /**
         * @type {Object} Defines the sizes for the e-picture component.
         */
        sizes: {
          0: 287,
          sm: 321,
          md: 215,
          lg: 362,
        },
      };
    },

    computed: {
      /**
       * Gets course date.
       *
       * @returns {String|null}
       */
      date() {
        return getCourseDateFormatted(this.course);
      },

      /**
       * Returns a formatted location string.
       *
       * @returns {String}
       */
      location() {
        const { name, street, zip, city, country } = this.course?.location || {};

        return [name, street, [zip, city].filter(Boolean).join(' '), country].filter(Boolean).join(', ');
      },

      /**
       * Gets the Google maps link.
       *
       * @returns {String|null}
       */
      mapsLink() {
        const { location } = this;

        return location ? this.$api.getUrl('ext-google-maps-search', { query: encodeURIComponent(location) }) : null;
      },

      /**
       * Returns if the course is on the favourites list.
       *
       * @returns {Boolean}
       */
      isFavourite() {
        return this.courseFavouritesStore.favourites?.includes(this.course?.id);
      },

      /**
       * Transforms the lecturer list to be passed to the overflow list component.
       *
       * @returns {Array}
       */
      lecturers() {
        return (this.course?.lecturers || []).map((lecturer) => ({
          text: this.getLectureFullName(lecturer),
          url: lecturer.url,
        }));
      },

      /**
       * Returns if the course is free.
       *
       * @returns {Boolean}
       */
      isFreeCourse() {
        return this.course.isFreeCourse;
      },

      /**
       * Returns if the course is on demand.
       *
       * @returns {Boolean}
       */
      isPriceOnDemand() {
        return this.course.isPriceOnDemand;
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() { },
    // beforeUpdate() {},
    // updated() { },
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Handler for favourite button.
       */
      onClickFavourite() {
        this.courseFavouritesStore.apiUpdateFavourite({
          courseId: this.course.id,
          isFavorite: !this.isFavourite,
        });
      },

      /**
       * Handles clicks on the cancel button.
       */
      onCancelButtonClick() {
        this.$emit('cancel', this.course);
      },

      /**
       * Returns the lecture name including the persons title.
       *
       * @param {Object} lecturer - A lecturer object.
       *
       * @returns {String}
       */
      getLectureFullName(lecturer) {
        if (typeof lecturer === 'string') {
          return lecturer;
        }

        const fullNameParts = [lecturer.name];

        if (lecturer.academicTitle) {
          fullNameParts.unshift(lecturer.academicTitle);
        }

        return fullNameParts.join(' ');
      },

      /**
       * Triggered when the professions label is clicked.
       *
       * @param {Object} profession - the selected profession.
       */
      onProfessionFilterClicked(profession) {
        this.$emit('professionFilterClicked', profession);
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-course-tile {
    $this: &;
    $grid-area--header: header;
    $grid-area--content: content;
    $grid-area--footer: footer;
    $grid-area--badges: badges;

    display: grid;
    grid-template-areas:
      '#{$grid-area--header}'
      '#{$grid-area--content}'
      '#{$grid-area--footer}';
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
    border: 1px solid variables.$color-grayscale--900;

    &__top {
      position: relative;
      grid-area: header;
    }

    &__image {
      display: block;
      overflow: hidden; // Clear float
      background: url('../assets/placeholder/placeholder--file.svg') no-repeat center center
        variables.$color-grayscale--920;
      background-size: 35%;

      &::before {
        content: '';
        float: left;
        width: 0;
        height: 0;
        padding-top: math.percentage(math.div(234, 375));
      }

      .e-picture {
        min-height: 1px;

        img {
          width: 100%;
          max-width: 100%;
          aspect-ratio: 375 / 234; // Fixes an issue with square dimension 'missing image' placeholders from Pimcore.
        }
      }
    }

    &__professions {
      position: absolute;
      top: functions.rem(variables.$spacing--10);
      left: functions.rem(variables.$spacing--20);
    }

    &__badges {
      position: relative;
      display: flex;
      grid-area: header;
      flex-wrap: wrap;
      justify-content: flex-end;
      align-self: end;
      margin-right: functions.rem(variables.$spacing--20);
      margin-bottom: functions.rem(variables.$spacing--25 * -1);
      justify-self: end;

      .e-badge {
        margin-bottom: functions.rem(variables.$spacing--5);
        margin-left: functions.rem(variables.$spacing--5);
      }
    }

    &__content {
      display: flex;
      grid-area: content;
      flex-direction: column;
      justify-content: space-between;
      padding: functions.rem(variables.$spacing--20);
    }

    &__content-bottom {
      display: flex;
      gap: functions.rem(variables.$spacing--5);
      justify-content: space-between;
      align-items: flex-start;
      margin-top: functions.rem(variables.$spacing--40);
      margin-bottom: 10px;
    }

    &__date {
      margin-bottom: functions.rem(variables.$spacing--30);
      color: variables.$color-grayscale--100;
    }

    &__meta {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: functions.rem(variables.$spacing--10);
    }

    &__seats {
      @include mixins.font(variables.$font-size--14);

      flex: 0 0 auto;
      margin-left: functions.rem(variables.$spacing--10);

      @include mixins.media(lg) {
        @include mixins.font-size(variables.$font-size--16, variables.$font-size--lg-scale-basis);
      }
    }

    &__title {
      @extend %heading-h3;
      @include mixins.font(variables.$font-size--24, null, variables.$font-weight--semi-bold);
      @include mixins.line-clamp(3, 1.2);

      margin-bottom: 0.3em;
    }

    &__lecturers {
      @include mixins.font(variables.$font-size--16, null, variables.$font-weight--semi-bold);

      color: variables.$color-grayscale--100;

      @include mixins.media(lg) {
        @include mixins.font-size(variables.$font-size--16, variables.$font-size--lg-scale-basis);
      }
    }

    &__lecturer {
      display: inline;
    }

    &__lecturer-link,
    &__lecturer-link:hover,
    &__lecturer-link:focus {
      .e-icon-sprite {
        width: functions.rem(12px);
        height: functions.rem(12px);
        margin-left: functions.rem(variables.$spacing--5);
      }
    }

    &__location-link:not(a) {
      cursor: default;
    }

    // stylelint-disable no-descending-specificity
    &__location,
    &__location-link,
    &__location-link:hover,
    &__location-link:focus {
      @include mixins.font(variables.$font-size--16);

      display: flex;
      align-items: center;
      align-self: end;
      color: variables.$color-grayscale--100;

      @include mixins.media(lg) {
        @include mixins.font-size(variables.$font-size--16, variables.$font-size--lg-scale-basis);
      }

      .e-icon-sprite {
        width: functions.rem(20px);
        height: functions.rem(20px);
        margin-right: functions.rem(variables.$spacing--5);
      }
    }

    &__price-suffix {
      @include mixins.font(variables.$font-size--16);

      color: variables.$color-grayscale--100;

      @include mixins.media(lg) {
        @include mixins.font-size(variables.$font-size--16, variables.$font-size--lg-scale-basis);
      }
    }

    &__actions {
      display: flex;
      grid-area: footer;
      border-top: 1px solid variables.$color-grayscale--900;
    }

    &__price {
      @include mixins.font(variables.$font-size--16);

      flex: 1 1 auto;
      color: variables.$color-primary--1;
      text-align: right;

      @include mixins.media(lg) {
        @include mixins.font-size(variables.$font-size--16, variables.$font-size--lg-scale-basis);
      }
    }

    &__cta {
      @extend %button-reset;

      position: relative;
      display: flex;
      flex: 1 0 auto;
      justify-content: center;
      align-items: center;
      min-width: 25%;
      padding: functions.rem(variables.$spacing--15);

      &:not(&--details):not(&--disabled) {
        max-width: 50%;
      }

      &::after {
        position: absolute;
        top: functions.rem(8px);
        right: 0;
        bottom: functions.rem(8px);
        content: '';
        width: 1px;
        background-color: variables.$color-grayscale--900;
      }

      &:last-child::after {
        display: none;
      }

      &:hover {
        .e-icon-sprite {
          color: variables.$color-secondary--6;
        }
      }

      .e-icon-sprite {
        width: functions.rem(20px);
        height: functions.rem(20px);
        color: variables.$color-primary--1;
      }

      &--disabled {
        max-width: 100%;
        background-color: functions.get-theme-var('e-button-primary-background-color--disabled');
        cursor: default;
        color: functions.get-theme-var('e-button-primary-text-color');
      }

      &--disabled:not(&--full-width) {
        min-width: 50%;
      }
    }

    &__cta--details {
      min-width: 50%;
      background-color: variables.$color-primary--1;
      color: variables.$color-grayscale--1000;
    }

    * + &__cta--details {
      max-width: 50%;
    }

    &__cta--details:hover,
    &__cta--details:focus {
      background-color: variables.$color-secondary--6;
      color: variables.$color-grayscale--1000;
    }

    &--variant-wide {
      @include mixins.media(md) {
        grid-template-areas:
          '#{$grid-area--header} #{$grid-area--badges}'
          '#{$grid-area--header} #{$grid-area--content}'
          '#{$grid-area--header} #{$grid-area--footer}';
        grid-template-columns: math.percentage(math.div(2, 3)) math.percentage(math.div(1, 3));
        grid-template-rows: auto 1fr auto;
      }

      #{$this}__badges {
        @include mixins.media(md) {
          grid-area: badges;
          align-self: start;
          justify-self: start;
          padding: functions.rem(variables.$spacing--20);
        }
      }

      .e-badge {
        @include mixins.media(md) {
          margin-right: functions.rem(variables.$spacing--5);
          margin-left: 0;
        }
      }
    }

    &--expand-badges:not(&--variant-wide) &__badges {
      flex-direction: column;
      align-items: flex-end;
    }
  }
</style>
